import React from 'react';
import { Platform, View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';

const IOS_MODAL_DISMISS_MS = 350;

export interface ChoiceDialogOption {
  label: string;
  onPress: () => void;
  destructive?: boolean;
}

export interface ChoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  message?: string;
  options: ChoiceDialogOption[];
  cancelLabel?: string;
  /** Só quando sai sem escolher (Voltar ou toque fora) — escolher uma opção não chama. */
  onCancel?: () => void;
}

/**
 * Lista de opções em modal ("Tirar foto" / "Escolher da galeria"...) — o par
 * do `ConfirmDialog` para quando são mais de duas saídas. Mesmo motivo:
 * Alert.alert é no-op no react-native-web.
 */
export function ChoiceDialog({
  open,
  onOpenChange,
  title,
  message,
  options,
  cancelLabel = 'Voltar',
  onCancel,
}: ChoiceDialogProps) {
  const cancel = () => {
    onOpenChange(false);
    onCancel?.();
  };

  return (
    <Dialog open={open} onOpenChange={(value) => (value ? onOpenChange(true) : cancel())}>
      <DialogContent className="gap-4 rounded-2xl" style={{ borderCurve: 'continuous' }}>
        <View className="gap-1">
          <ThemedText className="text-lg font-bold text-neutral-900">{title}</ThemedText>
          {message ? <ThemedText className="text-sm text-neutral-600">{message}</ThemedText> : null}
        </View>
        <View className="gap-2">
          {options.map((option) => (
            <Button
              key={option.label}
              title={option.label}
              variant={option.destructive ? 'destructive' : 'outline'}
              onPress={() => {
                onOpenChange(false);
                // iOS não apresenta câmera/galeria enquanto o Modal ainda
                // está fechando — espera a animação de saída terminar.
                if (Platform.OS === 'ios') setTimeout(option.onPress, IOS_MODAL_DISMISS_MS);
                else option.onPress();
              }}
            />
          ))}
          <Button title={cancelLabel} variant="ghost" onPress={cancel} />
        </View>
      </DialogContent>
    </Dialog>
  );
}
